import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Check, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { STATUTS_AFFECTATION_MODAL, STATUTS_CONFIG } from '@/constants/statuts';

interface Chantier {
  id: string;
  nom_chantier: string;
  client: string;
  etat_chantier: string;
}

interface ChantierGroupedSelectProps {
  chantiers: Chantier[];
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
}

export function ChantierGroupedSelect({
  chantiers,
  value,
  onValueChange,
  placeholder = 'Sélectionner un chantier',
}: ChantierGroupedSelectProps) {
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    STATUTS_AFFECTATION_MODAL.reduce((acc, s) => {
      acc[s.key] = s.defaultOpen;
      return acc;
    }, {} as Record<string, boolean>)
  );

  // Grouper les chantiers par statut, filtrer aux statuts pertinents
  const groupedChantiers = useMemo(() => {
    const statutKeys = STATUTS_AFFECTATION_MODAL.map(s => s.key);
    const groups: { key: string; label: string; icon: string; chantiers: Chantier[] }[] = [];

    for (const statutConfig of STATUTS_AFFECTATION_MODAL) {
      const config = STATUTS_CONFIG[statutConfig.key];
      if (!config) continue;

      const chantiersGroupe = chantiers.filter(c => c.etat_chantier === statutConfig.key);
      if (chantiersGroupe.length === 0) continue;

      groups.push({
        key: statutConfig.key,
        label: config.label,
        icon: config.icon,
        chantiers: chantiersGroupe,
      });
    }

    return groups;
  }, [chantiers]);

  // Trouver le chantier sélectionné pour l'affichage dans le trigger
  const selectedChantier = useMemo(
    () => chantiers.find(c => c.id === value),
    [chantiers, value]
  );

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleSelect = (chantierId: string) => {
    onValueChange(chantierId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selectedChantier && 'text-muted-foreground')}>
            {selectedChantier
              ? `${selectedChantier.nom_chantier} - ${selectedChantier.client}`
              : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 z-[200]" align="start">
        {groupedChantiers.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            Aucun chantier disponible
          </div>
        ) : (
          <div
            className="max-h-[300px] overflow-y-auto overscroll-contain"
            onWheel={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'];
              if (scrollKeys.includes(e.key)) {
                e.stopPropagation();
              }
            }}
          >
            {groupedChantiers.map((group) => (
              <div key={group.key}>
                {/* Header du groupe */}
                <button
                  type="button"
                  onClick={() => toggleSection(group.key)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
                >
                  {expandedSections[group.key] ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <span>{group.icon}</span>
                  {group.label}
                  <span className="text-xs text-muted-foreground ml-auto">
                    ({group.chantiers.length})
                  </span>
                </button>

                {/* Items du groupe */}
                {expandedSections[group.key] && (
                  <div className="py-1">
                    {group.chantiers.map((chantier) => (
                      <button
                        key={chantier.id}
                        type="button"
                        onClick={() => handleSelect(chantier.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-1.5 text-sm hover:bg-accent transition-colors',
                          value === chantier.id && 'bg-accent'
                        )}
                      >
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            value === chantier.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="truncate">
                          {chantier.nom_chantier} - {chantier.client}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
